import type { Address } from '@medplum/fhirtypes'
import type { ExtendedResource } from '@seen/core/ExtendedResource'
import { featureFlags } from '@seen/core/featureFlags'
import type { JsonObject } from '@seen/core/json'
import { Button } from '@seen/ui'
import { IconPlus, IconX } from '@tabler/icons-react'
import type { BaseSyntheticEvent, JSX } from 'react'
import { type Control, useFieldArray, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { AddressAutocomplete } from '#src/components/address/AddressAutocomplete.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

type AddressesResource = ExtendedResource & {
  addresses: Address[]
}

const USE_OPTIONS: {
  label: string
  value: NonNullable<Address['use']>
  keywords: string[]
}[] = [
  { label: 'Home', value: 'home', keywords: ['Home'] },
  { label: 'Work', value: 'work', keywords: ['Work'] },
  { label: 'Temp', value: 'temp', keywords: ['Temp'] },
  { label: 'Old', value: 'old', keywords: ['Old'] },
  { label: 'Billing', value: 'billing', keywords: ['Billing'] },
]

const TYPE_OPTIONS: {
  label: string
  value: NonNullable<Address['type']>
  keywords: string[]
}[] = [
  { label: 'Postal', value: 'postal', keywords: ['Postal'] },
  { label: 'Physical', value: 'physical', keywords: ['Physical'] },
  { label: 'Both', value: 'both', keywords: ['Both'] },
]

// Preserve positional `address.line` slots: line2 stays in slot 1 even when
// line1 is cleared (an empty string holds its place). Lines beyond line2 pass
// through untouched; only trailing empties are trimmed; all-empty clears the
// field.
function normalizeAddressLines(
  line: readonly (string | undefined)[] | undefined
): string[] | undefined {
  const lines = (line ?? []).map((entry) => entry ?? '')
  while (lines.length > 0 && !lines[lines.length - 1]) {
    lines.pop()
  }
  return lines.length > 0 ? lines : undefined
}

// `append` seeds `use`/`type`, so they don't count as content: a brand-new row
// is dropped, but a stored text-only or country-only address must survive.
function hasAddressContent(address: JsonObject): boolean {
  return Object.entries(address).some(([key, value]) => {
    if (key === 'use' || key === 'type') {
      return false
    }
    if (value == null || value === '') {
      return false
    }
    if (Array.isArray(value)) {
      return value.some((entry) => entry != null && entry !== '')
    }
    return true
  })
}

const schema = z.object({
  addresses: z.array(
    z.object({
      use: z.custom<NonNullable<Address['use']>>().optional(),
      type: z.custom<NonNullable<Address['type']>>().optional(),
      line: z.array(z.string()).optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postalCode: z.string().optional(),
    })
  ),
})

type AddressesFormValues = z.infer<typeof schema>

function AutocompleteRow({
  control,
  index,
  disabled,
  onChange,
}: {
  control: Control<AddressesFormValues>
  index: number
  disabled: boolean
  onChange: (address: Address) => void
}): JSX.Element {
  const row = useWatch({ control, name: `addresses.${index}` }) as Address
  return (
    <AddressAutocomplete
      value={hasAddressContent(row as JsonObject) ? row : undefined}
      disabled={disabled}
      // Typing a new address starts from an empty draft, so the row's use and
      // type are put back unless the saved address sets its own.
      onChange={(address) =>
        onChange({ use: row.use, type: row.type, ...address })
      }
    />
  )
}

function PlainAddressFields({
  control,
  index,
  disabled,
}: {
  control: Control<AddressesFormValues>
  index: number
  disabled: boolean
}): JSX.Element {
  return (
    <>
      <FormField
        control={control}
        name={`addresses.${index}.line.0`}
        disabled={disabled}
        render={({ field: { value, onChange, ...rest } }) => (
          <Input
            {...rest}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Address line"
          />
        )}
      />
      <FormField
        control={control}
        name={`addresses.${index}.line.1`}
        disabled={disabled}
        render={({ field: { value, onChange, ...rest } }) => (
          <Input
            {...rest}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Address line 2"
          />
        )}
      />
      <div className="grid grid-cols-3 gap-2">
        <FormField
          control={control}
          name={`addresses.${index}.city`}
          disabled={disabled}
          render={({ field: { value, onChange, ...rest } }) => (
            <Input
              {...rest}
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="City"
            />
          )}
        />
        <FormField
          control={control}
          name={`addresses.${index}.state`}
          disabled={disabled}
          render={({ field: { value, onChange, ...rest } }) => (
            <Input
              {...rest}
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="State"
            />
          )}
        />
        <FormField
          control={control}
          name={`addresses.${index}.postalCode`}
          disabled={disabled}
          render={({ field: { value, onChange, ...rest } }) => (
            <Input
              {...rest}
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="ZIP"
            />
          )}
        />
      </div>
    </>
  )
}

export function AddressesField<T extends AddressesResource>({
  instance,
  disabled = false,
  onDone,
}: {
  instance: T
  disabled?: boolean
  onDone?: () => void
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isDisabled = disabled || isReadOnly
  const autocomplete = useFeatureFlag(featureFlags.addressAutocomplete)

  const { mutate, isPending } = usePatchMutation<T>({
    mutation: {
      scope: { id: instance.reference.reference },
      onError: () => {
        toast.error('Failed to save address changes', {
          id: 'addresses-field-save-error',
        })
      },
    },
  })

  const form = useForm({
    schema,
    defaultValues: { addresses: instance.addresses },
  })
  const { control, getValues, reset, setValue } = form
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'addresses',
  })

  // Read straight from getValues — no Zod gate — so unregistered fields an
  // Address carries (text, country, period, ...) survive the save, and a row
  // with fewer than two lines (an undefined line slot) is not rejected.
  const save = (e?: BaseSyntheticEvent): void => {
    e?.preventDefault()
    const addresses = getValues()
      .addresses.map((address) => ({
        ...address,
        line: normalizeAddressLines(address.line),
      }))
      .filter(hasAddressContent) as Address[]
    mutate(
      {
        instance,
        mutateFn: (r) => {
          r.addresses = addresses
        },
      },
      { onSuccess: () => onDone?.() }
    )
  }

  const cancel = (): void => {
    reset({ addresses: instance.addresses })
    onDone?.()
  }

  return (
    <Form onSubmit={save} className="flex flex-col gap-3">
      {fields.map((row, idx) => (
        <div
          key={row.id}
          className="relative flex flex-col gap-2 rounded border p-3"
        >
          <div className="grid grid-cols-2 gap-2 pr-8">
            <FormField
              control={control}
              name={`addresses.${idx}.use`}
              disabled={isDisabled}
              render={({ field: { value, onChange, ...rest } }) => (
                <Combobox<NonNullable<Address['use']>>
                  {...rest}
                  options={USE_OPTIONS}
                  onSelect={(selected) => onChange(selected ?? 'work')}
                >
                  <Button variant="outline" className="w-full justify-start">
                    {USE_OPTIONS.find((o) => o.value === value)?.label ?? 'Use'}
                  </Button>
                </Combobox>
              )}
            />
            <FormField
              control={control}
              name={`addresses.${idx}.type`}
              disabled={isDisabled}
              render={({ field: { value, onChange, ...rest } }) => (
                <Combobox<NonNullable<Address['type']>>
                  {...rest}
                  options={TYPE_OPTIONS}
                  onSelect={(selected) => onChange(selected ?? 'both')}
                >
                  <Button variant="outline" className="w-full justify-start">
                    {TYPE_OPTIONS.find((o) => o.value === value)?.label ??
                      'Type'}
                  </Button>
                </Combobox>
              )}
            />
          </div>
          {autocomplete ? (
            <AutocompleteRow
              control={control}
              index={idx}
              disabled={isDisabled}
              onChange={(address) =>
                setValue(
                  `addresses.${idx}`,
                  address as AddressesFormValues['addresses'][number],
                  { shouldDirty: true }
                )
              }
            />
          ) : (
            <PlainAddressFields
              control={control}
              index={idx}
              disabled={isDisabled}
            />
          )}
          {!isDisabled && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1 right-1"
              onClick={() => remove(idx)}
            >
              <IconX className="size-4" />
            </Button>
          )}
        </div>
      ))}
      {!isDisabled && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => append({ use: 'work', type: 'both' })}
          >
            <IconPlus className="mr-1 size-4" /> Add address
          </Button>
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={cancel}>
              Cancel
            </Button>
            <SubmitButton size="sm" submitting={isPending}>
              Save
            </SubmitButton>
          </div>
        </>
      )}
    </Form>
  )
}
